import React, { useContext, useCallback, useState, useEffect, useRef } from 'react'
import { Button, Badge, App, Tooltip } from 'antd'
import { useFeatureFlagEnabled } from 'posthog-js/react'
import { useManualQuery } from 'graphql-hooks'
import difference from 'lodash/difference'

import { RightActionBarContext } from '../../context/rightActionBarContext'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectAuthUserSlice } from '../../store/auth-user-slice'
import { selectAuthCompanySlice } from '../../store/auth-company-slice'
import { selectLocaleSlice } from '../../store/locale-slice'
import { selectWhatsNewsSlice, setWhatsNewId as setReduxWhatsNewData } from '../../store/whats-news-slice'
import { getWhatsNew as getWhatsNewQuery } from '../../graphql/custom-queries'
import { setWhatsNewAsRead } from '../../graphql/custom-mutation'
import { wait } from '@vacationtracker/shared/functions/wait'
import { track } from '../../services/analytics/analytics'
import { useHeatmapData } from '../../routes/DashboardPage/hooks/useDashboardQueries'
import {
  getApproverOnboardingProgress,
  shouldShowApproverOnboardingProgress,
  clearApproverOnboardingProgress,
  APPROVER_ONBOARDING_PROGRESS_EVENT
} from '../../util/approver-onboarding-storage'

import rightActionBarMenuItems from './right-actionbar-menu'
import { rightActionBarTracker } from './services/rightActionBarTracking'
import { HelpDrawerContent } from '../../components/help-drawer-content'
import { DocsDrawerContent } from '../../components/docs-drawer-content'
import { WhatsNewDrawerContent } from '../../components/whats-new-drawer-content'
import { AnnouncementModal } from '../../components/announcement-modal'
import { MSBotSetupDrawerContent } from '../../components/ms-bot-setup-drawer-content'
import { PopupNewsModal } from '../../components/popup-news-modal'
import { OnboardingV2ProgressButton } from '../../components/onboarding-v2-progress-button'
import { ApproverOnboardingProgressButton } from '../../components/approver-onboarding-progress-button'
import { RightActionBarOnboardingProgress } from '../../components/right-actionbar-onboarding-progress'
import { OnboardingADP, OnboardingADPConnector } from '../../components/adp/onboarding-adp'

import { IRightActionBarItem, RightActionBarItemKeys } from '../../types/right-action-bar'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import { getOnboardingV2Progress, shouldShowOnboardingV2Progress, ONBOARDING_V2_PROGRESS_EVENT } from '../../util/onboarding-v2-storage'
import { IWhatsNewComponent, IWhatsNewQuery } from '@vacationtracker/shared/types/whats-new'

const RightActionBar = (): React.ReactElement | null => {
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { locale } = useAppSelector(selectLocaleSlice)
  const { whatsNewsIds } = useAppSelector(selectWhatsNewsSlice)

  // Fetch holidays data for onboarding
  const { data: heatmapData } = useHeatmapData()
  const {
    rightActionBarVisible,
    openDrawer,
    activeDrawer,
    badges,
    closeDrawer,
  } = useContext(RightActionBarContext)

  const onboardingV2Enabled = useFeatureFlagEnabled(FeatureFlagEnum.onboardingV2)
  const { modal } = App.useApp()
  const dispatch = useAppDispatch()
  const isRightNavbarMounted = useRef(false)

  const [getWhatsNewGqlQuery] = useManualQuery<IWhatsNewQuery, { limit: number }>(getWhatsNewQuery)
  const [setWhatsNewAsReadQuery] = useManualQuery(setWhatsNewAsRead)
  // State to track onboarding progress updates
  const [onboardingProgress, setOnboardingProgress] = useState(() =>
    getOnboardingV2Progress(authUser.id, authCompany?.id as string)
  )
  const [approverProgress, setApproverProgress] = useState(() =>
    getApproverOnboardingProgress(authUser.id, authCompany?.id as string)
  )

  // State for Announcements and WhatsNew badges/popups
  const [showAnnouncement, setShowAnnouncement] = useState(false)
  const [whatsNewData, setWhatsNewData] = useState<IWhatsNewComponent[] | []>([])
  const [unreadWhatsNews, setUnreadWhatsNews] = useState(0)
  const [showPopupNewsModal, setShowPopupNewsModal] = useState(true)
  const [showPopupNews, setShowPopupNews] = useState<IWhatsNewComponent | null>(null)

  // Copy exact effects from Topbar
  useEffect(() => {
    isRightNavbarMounted.current = true
    rightActionBarTracker.init()
    return () => {
      isRightNavbarMounted.current = false
    }
  }, [])

  useEffect(() => {
    getWhatsNew()
  }, [authUser.id])

  useEffect(() => {
    if (isRightNavbarMounted.current && whatsNewsIds.length > 0 && difference(whatsNewsIds, whatsNewData.map(item => item.messageId)).length > 0) {
      getWhatsNew()
    }
  }, [whatsNewsIds])

  const getWhatsNew = async () => {
    const response = await getWhatsNewGqlQuery({ variables: { limit: 5 }})
    if (!isRightNavbarMounted.current) return
    if (!response.data || response.error) throw response.error
    setWhatsNewData(response.data?.getWhatsNew?.news)
    const unreadNews = response.data?.getWhatsNew?.news.filter(news => news.unread)
    setUnreadWhatsNews(unreadNews.length)
    if (unreadNews.length > 0 && unreadNews.find(news => news.showPopup)) {
      setShowPopupNewsModal(true)
      setShowPopupNews(unreadNews.find(news => news.showPopup) as IWhatsNewComponent)
    }
    dispatch(setReduxWhatsNewData(response.data?.getWhatsNew?.news.map(item => item.messageId)))
  }

  const handleAnnouncement = () => {
    setShowAnnouncement(!showAnnouncement)
  }

  const handleUnreadNews = async () => {
    await Promise.all(whatsNewData.map(async ({ messageId, unread }: IWhatsNewComponent) => {
      if (unread) {
        await setWhatsNewAsReadQuery({ variables: { messageId }})
        setUnreadWhatsNews((news => news - 1))
        wait(3000)
      }
      return Promise.resolve()
    }))
  }

  // Listen for onboarding progress updates
  useEffect(() => {
    const handleOnboardingUpdate = () => {
      setOnboardingProgress(getOnboardingV2Progress(authUser.id, authCompany?.id as string))
    }

    const handleApproverUpdate = () => {
      setApproverProgress(getApproverOnboardingProgress(authUser.id, authCompany?.id as string))
    }

    window.addEventListener(ONBOARDING_V2_PROGRESS_EVENT, handleOnboardingUpdate)
    window.addEventListener(APPROVER_ONBOARDING_PROGRESS_EVENT, handleApproverUpdate)

    return () => {
      window.removeEventListener(ONBOARDING_V2_PROGRESS_EVENT, handleOnboardingUpdate)
      window.removeEventListener(APPROVER_ONBOARDING_PROGRESS_EVENT, handleApproverUpdate)
    }
  }, [authUser.id, authCompany?.id])

  // Check if user is still an approver and clear progress if not
  useEffect(() => {
    const isUserApprover = (authUser.approverToTeams?.length || 0) > 0

    // If user has approver onboarding progress but is no longer an approver, clear it
    if (approverProgress && !isUserApprover) {
      clearApproverOnboardingProgress(authUser.id, authCompany?.id as string)
      setApproverProgress(null)
    }
  }, [authUser.id, authUser.approverToTeams, authCompany?.id, approverProgress])


  // Filter menu items based on user role, platform, and feature flags
  const filterMenuItem = useCallback((item: IRightActionBarItem): boolean => {
    // Check user role
    if (item.roles && !item.roles.includes(authUser.role)) {
      return false
    }

    // Check platform
    if (item.platform && !item.platform.includes(authUser.platform)) {
      return false
    }

    return true
  }, [authUser])

  const getDrawerContent = useCallback((key: string): React.ReactNode => {
    switch (key) {
      case RightActionBarItemKeys.HELP:
        return <HelpDrawerContent onClose={closeDrawer} />
      case RightActionBarItemKeys.DOCS:
        return <DocsDrawerContent onClose={closeDrawer} />
      case RightActionBarItemKeys.WHATS_NEW:
        return <WhatsNewDrawerContent onClose={closeDrawer} />
      case RightActionBarItemKeys.MS_BOT_SETUP:
        if (authUser.role === 'Admin') {
          return <MSBotSetupDrawerContent onClose={closeDrawer} />
        }
        return null
      default:
        return null
    }
  }, [closeDrawer, authUser.role])

  const handleItemClick = useCallback((item: IRightActionBarItem) => {
    // If item has custom onClick, use that
    if (item.onClick) {
      item.onClick()
      return
    }

    // Special handling for Announcements - trigger modal like Topbar
    if (item.key === RightActionBarItemKeys.ANNOUNCEMENTS) {
      track('ANNOUNCEMENT_CLICKED', { platform: authUser.platform })
      handleAnnouncement()
      return
    }

    // Track drawer openings
    switch (item.key) {
      case RightActionBarItemKeys.MS_BOT_SETUP:
        rightActionBarTracker.trackInstallBotOpened({ userRole: authUser.role })
        break
      case RightActionBarItemKeys.HELP:
        rightActionBarTracker.trackHelpdeskOpened()
        break
      case RightActionBarItemKeys.DOCS:
        // Tracking happens in DocsDrawerContent when content loads with contextual article info
        break
      case RightActionBarItemKeys.WHATS_NEW:
        rightActionBarTracker.trackLatestFeaturesOpened({ unreadCount: unreadWhatsNews })
        break
    }

    // Otherwise, open drawer with content
    const content = getDrawerContent(item.key)
    if (content) {
      openDrawer(item.key, item.title, content)
    }
  }, [openDrawer, getDrawerContent, handleAnnouncement, authUser.platform, authUser.role, unreadWhatsNews])

  // Hide on mobile/tablet (768px and below)
  if (window.innerWidth < 768 || !rightActionBarVisible) {
    return null
  }

  const visibleItems = rightActionBarMenuItems.filter(filterMenuItem)

  // Helper function to render onboarding progress
  const renderOnboardingProgress = () => {
    // Priority logic: Admin onboarding takes precedence over approver onboarding
    const isAdmin = authUser.role === 'Admin'
    const isUserApprover = (authUser.approverToTeams?.length || 0) > 0

    // Check for admin onboarding first (highest priority)
    const shouldShowAdminOnboarding = onboardingV2Enabled &&
      shouldShowOnboardingV2Progress(onboardingProgress) && isAdmin

    // Check for approver onboarding only if no admin onboarding
    // AND verify user is actually an approver
    const shouldShowApproverOnboarding = !shouldShowAdminOnboarding &&
      shouldShowApproverOnboardingProgress(approverProgress, isUserApprover, authCompany?.plan)

    if (!shouldShowAdminOnboarding && !shouldShowApproverOnboarding) {
      return null
    }

    let currentStep, totalSteps, onClickHandler

    if (shouldShowAdminOnboarding) {
      totalSteps = Number(onboardingProgress?.totalSteps || 3)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      currentStep = Math.min((onboardingProgress?.completedSteps?.length || 0) + 1, totalSteps)
      onClickHandler = () => {
        rightActionBarTracker.trackOnboardingOpened({
          onboardingType: 'admin',
          currentStep,
          totalSteps,
        })
        import('../../components/onboarding-v2-modal').then(({ showOnboardingV2Modal }) => {
          showOnboardingV2Modal(modal, authUser, locale, {
            locations: heatmapData?.locations || [],
            departments: heatmapData?.departments || [],
            labels: heatmapData?.labels || [],
            holidays: heatmapData?.holidays || [],
          })
        }).catch(() => {
          const hiddenDiv = document.createElement('div')
          hiddenDiv.style.display = 'none'
          document.body.appendChild(hiddenDiv)
          import('react-dom/client').then(ReactDOMClient => {
            const root = ReactDOMClient.createRoot(hiddenDiv)
            root.render(React.createElement(OnboardingV2ProgressButton, {
              authUser, locale,
              heatmapData: {
                locations: heatmapData?.locations || [],
                departments: heatmapData?.departments || [],
                labels: heatmapData?.labels || [],
                holidays: heatmapData?.holidays || [],
              },
            }))
            setTimeout(() => {
              const button = hiddenDiv.querySelector('button')
              if (button) button.click()
              root.unmount()
              document.body.removeChild(hiddenDiv)
            }, 10)
          })
        })
      }
    } else if (shouldShowApproverOnboarding) {
      currentStep = approverProgress?.currentStep || 1
      totalSteps = 3
      onClickHandler = () => {
        rightActionBarTracker.trackOnboardingOpened({
          onboardingType: 'approver',
          currentStep,
          totalSteps,
        })
        import('../../components/approver-onboarding-modal').then(({ showApproverOnboardingModal }) => {
          // Get all department names where user is an approver
          const departmentNames = authUser?.approverToTeams
            ?.map(team => team.name)
            .filter((name): name is string => Boolean(name && typeof name === 'string')) || []
          showApproverOnboardingModal(modal, authUser, locale, departmentNames, {
            companyId: authCompany?.id || '',
          })
        }).catch(() => {
          const hiddenDiv = document.createElement('div')
          hiddenDiv.style.display = 'none'
          document.body.appendChild(hiddenDiv)
          import('react-dom/client').then(ReactDOMClient => {
            const root = ReactDOMClient.createRoot(hiddenDiv)
            root.render(React.createElement(ApproverOnboardingProgressButton, {
              authUser, locale, companyId: authCompany?.id || '', companyPlan: authCompany?.plan,
            }))
            setTimeout(() => {
              const button = hiddenDiv.querySelector('button')
              if (button) button.click()
              root.unmount()
              document.body.removeChild(hiddenDiv)
            }, 10)
          })
        })
      }
    }

    return (
      <div className="right-actionbar-item">
        <RightActionBarOnboardingProgress
          currentStep={currentStep}
          totalSteps={totalSteps}
          onClick={onClickHandler}
        />
      </div>
    )
  }

  return (
    <div className="right-actionbar">
      <div className="right-actionbar-content">
        {visibleItems.map((item) => {
          const isActive = activeDrawer === item.key
          let badge = badges[item.key]

          // Use unread count for What's New badge (same as Topbar)
          if (item.key === RightActionBarItemKeys.WHATS_NEW && unreadWhatsNews > 0) {
            badge = unreadWhatsNews
          }

          return (
            <div key={item.key} className="right-actionbar-item">
              <Tooltip placement="left" title={item.title}>
                <Badge
                  dot={Boolean(badge) || Boolean(item.badge) || false}
                  size="small"
                  className="right-actionbar-badge"
                >
                  <Button
                    type="text"
                    size="large"
                    onClick={() => handleItemClick(item)}
                    className={`right-actionbar-button ${isActive ? 'active' : ''}`}
                  >
                    <span className="right-actionbar-icon">{item.icon}</span>
                    <span className="right-actionbar-text">{item.title}</span>
                  </Button>
                </Badge>
              </Tooltip>
            </div>
          )
        })}

        {/* Product Tour - Integrated with other items */}
        {renderOnboardingProgress()}
      </div>

      {/* Announcement Modal - same as Topbar */}
      {showAnnouncement &&
        <AnnouncementModal
          visible={showAnnouncement}
          onCancel={handleAnnouncement}
          platform={authCompany?.platform || ''}
        />
      }

      {/* ADP Onboarding Components - moved from Topbar */}
      <OnboardingADP
        visible={
          authUser.role === 'Admin' &&
          Boolean(authCompany?.integrationOnboardings?.some(onboarding => onboarding.name === 'ADP')) &&
          Number(authCompany?.integrationOnboardings?.find(onboarding => onboarding.name === 'ADP')?.completedPercentage) < 100
        }
        completedPercentage={authCompany?.integrationOnboardings?.find(onboarding => onboarding.name === 'ADP')?.completedPercentage || 0}
      />
      <OnboardingADPConnector
        visible={
          authUser.role === 'Admin' &&
          Boolean(authCompany?.integrationOnboardings?.some(onboarding => onboarding.name === 'ADP-CONNECTOR')) &&
          Number(authCompany?.integrationOnboardings?.find(onboarding => onboarding.name === 'ADP-CONNECTOR')?.completedPercentage) < 100
        }
        completedPercentage={authCompany?.integrationOnboardings?.find(onboarding => onboarding.name === 'ADP-CONNECTOR')?.completedPercentage || 0}
      />

      {/* PopupNewsModal - moved from Topbar */}
      <PopupNewsModal
        showPopupNewsModal={showPopupNewsModal}
        news={showPopupNews}
        onCancel={() => {
          setShowPopupNewsModal(false)
          handleUnreadNews()
        }}
      />
    </div>
  )
}

export default RightActionBar